import { IProfileChild, IProfileOptionsResponse } from '@kcftech/prometheus-client';
import { BurstDataType } from 'pages/workbench/types';
import { round } from 'utils/number';
import { Lookup } from 'utils/types';

/**
 * Converts a Celsius temperature to Fahrenheit temperature.
 * @param celsius The celsius value to convert to Fahrenheit.
 */
export const celsiusToFahrenheit = (celsius: number): number => celsius * (9 / 5) + 32;

/**
 * Converts a Fahrenheit temperature to Celsius temperature.
 * @param celsius The Fahrenheit temperature to convert to Celsius.
 */
export const fahrenheitToCelsius = (fahrenheit: number): number =>
    (fahrenheit - 32) * (5 / 9);

/**
 * Checks if the given units are Celsius temperature units.
 * @param unit The units to check.
 */
export const isCelsiusUnit = (unit: string | undefined) =>
    unit !== undefined ? /^º?(c|celsius)$/i.test(unit) : false;

/**
 * Checks if the given units are Fahrenheit temperature units.
 * @param unit The units to check.
 */
export const isFahrenheitUnit = (unit: string | undefined) =>
    unit !== undefined ? /^º?(f|fahrenheit)$/i.test(unit) : false;

export const hzToCpm = (hz: number): number => hz * 60;
export const cpmToHz = (cpm: number): number => cpm / 60;

/**
 * Gets the running speed factor used by the trend to display running speed
 * @param runningSpeedUnit The running speed unit
 * @param runningSpeedHz  The running speed in hz
 * @returns The running speed factor
 */
export const getRunningSpeedFactor = (
    runningSpeedUnit: string | undefined,
    runningSpeedHz: number | undefined,
): number => {
    if (runningSpeedUnit === undefined) {
        return 0;
    }
    switch (runningSpeedUnit.toLowerCase()) {
        case 'cpm':
        case 'cyclesperminute':
        case 'cyclespermin': // this is what the api gives us back
            return 60;
        case 'order':
        case 'orders':
            return runningSpeedHz === undefined || runningSpeedHz === 0
                ? 0
                : 1 / runningSpeedHz;
        case 'hertz':
            return 1;
        default:
            throw new Error('Invalid running speed unit');
    }
};

/**
 * Gets the abbreviation to display for the running speed unit
 * @param runningSpeedUnit The running speed from the api
 * @returns  CPM | Hz | ''
 */
export const getRunningSpeedDisplayUnits = (
    runningSpeedUnit: string | undefined,
): string => {
    if (runningSpeedUnit === undefined) {
        return '';
    }
    switch (runningSpeedUnit.toLowerCase()) {
        case 'cpm':
        case 'cyclesperminute':
        case 'cyclespermin': // this is what the api gives us back
            return 'RPM/CPM';
        case 'order':
        case 'orders':
            // no units when we are displaying in orders
            return '';
        case 'hertz':
            return 'Hz';
        default:
            throw new Error('Invalid running speed unit');
    }
};

/**
 * Gets the converted running speed value based on the unit
 * @param runningSpeedUnit The unit from the api
 * @param runningSpeedHz The running speed in Hz
 * @returns The converted value
 */
export const getConvertedRunningSpeed = (
    runningSpeedUnit: string | undefined,
    runningSpeedHz: number | undefined,
): number | undefined => {
    if (runningSpeedUnit === undefined || runningSpeedHz === undefined) {
        return undefined;
    }
    switch (runningSpeedUnit.toLowerCase()) {
        case 'cyclesperminute':
        case 'cyclespermin': // this is what the api gives us back
            return round(hzToCpm(runningSpeedHz), 4);
        case 'hertz':
            return runningSpeedHz;
        default:
            throw new Error('Invalid running speed unit');
    }
};

/**
 * Sets the converted running speed value based on the unit
 * @param runningSpeedUnit The unit to set to
 * @param runningSpeedHz The running speed in CPM
 * @returns The converted value
 */
export const setConvertedRunningSpeed = (
    runningSpeedUnit: string | undefined,
    runningSpeed: number | undefined,
): number | undefined => {
    if (runningSpeedUnit === undefined || runningSpeed === undefined) {
        return undefined;
    }
    switch (runningSpeedUnit) {
        case 'CyclesperMin':
            return cpmToHz(runningSpeed);
        case 'Hertz':
            return runningSpeed;
        default:
            throw new Error('Invalid running speed unit');
    }
};

/**
 * Gets the display units for the given burst data type based on the user's profile selection
 * @param burstDataType The burst data type
 * @param profileOptions The map of api format units to human readable units
 * @param profile The user's profile
 * @returns The display units for the burst data type
 */
export const getBurstDataUnits = (
    burstDataType: BurstDataType,
    profileOptions: IProfileOptionsResponse | undefined,
    profile: IProfileChild | undefined,
) => {
    switch (burstDataType) {
        case 'acceleration':
            return (
                profileOptions?.AccelerationUnits.find(
                    (option) => option.Value === profile?.AccelerationUnits,
                )?.Text ?? profileOptions?.AccelerationUnits[0].Text
            );
        case 'velocity':
            return (
                profileOptions?.VelocityUnits.find(
                    (option) => option.Value === profile?.VelocityUnits,
                )?.Text ?? profileOptions?.VelocityUnits[0].Text
            );
        default:
            throw new Error('Unrecognized burst data type');
    }
};

const ticksPerUnit: Lookup<DurationUnit, number> = {
    milliseconds: 1,
    seconds: 1000,
    minutes: 1000 * 60,
    hours: 1000 * 60 * 60,
    days: 1000 * 60 * 60 * 24,
};

type DurationUnit = 'milliseconds' | 'seconds' | 'minutes' | 'hours' | 'days';

/**
 * Converts a duration value from one type to another.
 *
 * @param value number of string to convert.
 * @param from unit converting from.
 * @param to unit converting to.
 * @returns the converted unit.
 */
export const convertDuration = (
    value: number | string,
    from: DurationUnit,
    to: DurationUnit,
): number => {
    const parsedValue = Number(value);

    return from === to
        ? parsedValue
        : (parsedValue * ticksPerUnit[from]) / ticksPerUnit[to];
};
